import { motion } from "framer-motion";

const CircuitBoard = ({ className = "" }: { className?: string }) => {
  const paths = [
    "M0 200 H120 V80 H250 V200 H400",
    "M0 350 H80 V250 H200 V350 H320 V280 H500",
    "M100 0 V100 H300 V200",
    "M400 0 V150 H500",
    "M0 500 H150 V400 H350 V500",
    "M250 0 V50 H450 V150 H500",
    "M0 100 H60 V300 H180",
    "M500 300 H380 V420 H200 V500",
  ];

  const nodes = [
    { cx: 120, cy: 80 },
    { cx: 250, cy: 200 },
    { cx: 80, cy: 250 },
    { cx: 320, cy: 280 },
    { cx: 300, cy: 200 },
    { cx: 150, cy: 400 },
    { cx: 450, cy: 150 },
    { cx: 60, cy: 300 },
    { cx: 380, cy: 420 },
    { cx: 200, cy: 350 },
  ];

  return (
    <svg
      viewBox="0 0 500 500"
      fill="none"
      className={`absolute pointer-events-none ${className}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      {paths.map((d, i) => (
        <motion.path
          key={i}
          d={d}
          stroke="url(#circuitGrad)"
          strokeWidth="1"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.3 }}
          transition={{ duration: 2 + i * 0.3, delay: i * 0.2, ease: "easeInOut" }}
        />
      ))}
      {/* Animated pulse traveling along paths */}
      {paths.slice(0, 4).map((d, i) => (
        <motion.circle
          key={`pulse-${i}`}
          r="2"
          fill="hsl(185 100% 55%)"
          filter="url(#glow)"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{ duration: 3, delay: 1 + i * 0.8, repeat: Infinity, repeatDelay: 2 }}
        >
          <animateMotion dur={`${4 + i}s`} repeatCount="indefinite" begin={`${i * 0.5}s`}>
            <mpath href={`#travelPath${i}`} />
          </animateMotion>
        </motion.circle>
      ))}
      {/* Travel paths (hidden, used for animateMotion) */}
      {paths.slice(0, 4).map((d, i) => (
        <path key={`tp-${i}`} id={`travelPath${i}`} d={d} fill="none" />
      ))}
      {/* Junction nodes with pulse */}
      {nodes.map((node, i) => (
        <g key={`node-${i}`}>
          <motion.circle
            cx={node.cx}
            cy={node.cy}
            r="3"
            fill="hsl(185 100% 55%)"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.6, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.5 + i * 0.15 }}
          />
          <motion.circle
            cx={node.cx}
            cy={node.cy}
            r="8"
            fill="none"
            stroke="hsl(185 100% 55%)"
            strokeWidth="0.5"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 0.4, 0], scale: [0.5, 1.5, 2] }}
            transition={{ duration: 2.5, delay: 2 + i * 0.3, repeat: Infinity, repeatDelay: 3 }}
          />
        </g>
      ))}
      <defs>
        <linearGradient id="circuitGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(185 100% 55%)" />
          <stop offset="100%" stopColor="hsl(270 85% 65%)" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  );
};

export default CircuitBoard;
